// src/pages/stock/EsimInventories.tsx
//
// Inventory list page
// - Uses shared table components (toolbar / frame / pagination).
// - Keeps URL query params as the single source of truth for filters + pagination.
// - Converts date filters between UI (YYYY-MM-DD) and API (timestamp string).
// - Navigates to detail page via the index (#) column.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import {
  EsimInventoryParameterType,
  exportEsimInventoriesExcel,
  type EsimInventoryResponse,
  type GetEsimInventoriesParams,
} from '@apis/esim';

import { EsimStatus, EsimType } from '@models/stock';
import { ESIM_TYPE_OPTION_LIST } from '@constants/stocks';
import {
  getEsimStatusLabel,
  getEsimStatusMeta,
} from '@constants/stockLabels';

import { useInventoryList } from '@queries/inventories/useInventoryList';
import { useInventoryCount } from '@queries/inventories/useInventoryCount';
import { useAllStockList } from '@queries/stocks/useAllStockList';

import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';
import { dateToTimestamp, timestampToDate } from '@utils/time';

import EsimInventoriesSearchPanel from '@components/stock/EsimInventoriesSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';
import { usePageHeader } from '@components/common/pageHeader.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

type DraftSearchForm = GetEsimInventoriesParams;
type Option<T> = { label: string; value: T };

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const dashIfEmpty = (v: unknown): string => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const isValidEnumValue = <T extends string>(
  values: readonly string[],
  v: string | null,
): v is T => {
  if (!v) return false;
  return values.includes(v);
};

const safeTimestampToYmd = (ts: string | null | undefined): string | undefined => {
  if (!ts) return undefined;
  try {
    const ymd = timestampToDate(ts, 'YYYY-MM-DD');
    return ymd ? String(ymd) : undefined;
  } catch {
    return undefined;
  }
};

const ymdToTimestampParam = (
  ymd?: string | null,
  isEnd?: boolean,
): string | undefined => {
  const v = normalizeText(ymd);
  if (!v) return undefined;

  try {
    // For end date, include end-of-day; for start date, include start-of-day.
    const ts = isEnd ? dateToTimestamp(v, true, true) : dateToTimestamp(v, false, true, true);
    return String(ts);
  } catch {
    return undefined;
  }
};

/**
 * Convert draft (UI state) into applied (API) params.
 * - Trims text inputs
 * - Converts expireAtFrom/To from YYYY-MM-DD -> timestamp string
 * - Keeps parameterValue as string (API contract)
 */
const toAppliedParams = (draft: DraftSearchForm): GetEsimInventoriesParams => {
  const parameterValue = normalizeText(draft.parameterValue);

  return {
    stockId: typeof draft.stockId === 'number' ? draft.stockId : undefined,
    esimType: draft.esimType || undefined,
    esimStatus: draft.esimStatus || undefined,
    expireAtFrom: ymdToTimestampParam(draft.expireAtFrom) || undefined,
    expireAtTo: ymdToTimestampParam(draft.expireAtTo, true) || undefined,
    parameterType: draft.parameterType || EsimInventoryParameterType.LPA_URL,
    parameterValue,
  };
};

const readDraftFromUrl = (sp: URLSearchParams): DraftSearchForm => {
  const esimTypeRaw = sp.get('esimType');
  const esimStatusRaw = sp.get('esimStatus');
  const parameterTypeRaw = sp.get('parameterType');

  const esimType = isValidEnumValue<EsimType>(
    Object.values(EsimType) as unknown as string[],
    esimTypeRaw,
  )
    ? (esimTypeRaw as EsimType)
    : undefined;

  const esimStatus = isValidEnumValue<EsimStatus>(
    Object.values(EsimStatus) as unknown as string[],
    esimStatusRaw,
  )
    ? (esimStatusRaw as EsimStatus)
    : undefined;

  const parameterType = isValidEnumValue<EsimInventoryParameterType>(
    Object.values(EsimInventoryParameterType) as unknown as string[],
    parameterTypeRaw,
  )
    ? (parameterTypeRaw as EsimInventoryParameterType)
    : EsimInventoryParameterType.LPA_URL;

  return {
    stockId: parseNumberParam(sp.get('stockId')),
    esimType,
    esimStatus,
    expireAtFrom: safeTimestampToYmd(sp.get('expireAtFrom')),
    expireAtTo: safeTimestampToYmd(sp.get('expireAtTo')),
    parameterType,
    parameterValue: sp.get('parameterValue') || '',
  };
};

const buildUrlParams = (draft: DraftSearchForm, page: number) => {
  const applied = toAppliedParams(draft);

  const params: Record<string, string> = {};
  if (typeof applied.stockId === 'number') params.stockId = String(applied.stockId);
  if (applied.esimType) params.esimType = String(applied.esimType);
  if (applied.esimStatus) params.esimStatus = String(applied.esimStatus);

  if (applied.expireAtFrom) params.expireAtFrom = String(applied.expireAtFrom);
  if (applied.expireAtTo) params.expireAtTo = String(applied.expireAtTo);

  if (applied.parameterType) params.parameterType = String(applied.parameterType);

  const keyword = normalizeText(applied.parameterValue);
  if (keyword) params.parameterValue = keyword;

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const renderStatusChip = (raw: unknown) => {
  const key = String(raw ?? '').trim() as EsimStatus;
  const meta = getEsimStatusMeta(key);

  return (
    <Chip
      size="small"
      label={key ? meta.label : dashIfEmpty(raw)}
      color={meta.chip.color}
      variant={meta.chip.variant}
      sx={{ fontWeight: 800 }}
    />
  );
};

const ESIM_TYPE_LABEL_MAP: Record<string, string> = (
  ESIM_TYPE_OPTION_LIST as Array<Option<EsimType>>
).reduce((acc, cur) => {
  acc[String(cur.value)] = cur.label;
  return acc;
}, {} as Record<string, string>);

const formatEsimType = (v: unknown) => {
  const key = String(v ?? '').trim();
  if (!key) return '-';
  return ESIM_TYPE_LABEL_MAP[key] ?? key;
};

const EsimInventories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageHeaderSlots } = usePageHeader();

  useEffect(() => {
    // This page does not provide extra header actions.
    setPageHeaderSlots({});
  }, [setPageHeaderSlots]);

  const [draftSearch, setDraftSearch] = useState<DraftSearchForm>(() =>
    readDraftFromUrl(searchParams),
  );

  const [appliedSearch, setAppliedSearch] = useState<GetEsimInventoriesParams>(() =>
    toAppliedParams(readDraftFromUrl(searchParams)),
  );

  const [page, setPage] = useState<number>(() => {
    const raw = searchParams.get('page');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  /**
   * Keep local UI state in sync with URL params.
   * URL params are treated as the single source of truth.
   */
  const searchParamsKey = useMemo(() => searchParams.toString(), [searchParams]);

  useEffect(() => {
    const nextDraft = readDraftFromUrl(searchParams);

    const raw = searchParams.get('page');
    const n = Number(raw);
    const nextPage = Number.isFinite(n) && n > 0 ? n : 1;

    setDraftSearch(nextDraft);
    setAppliedSearch(toAppliedParams(nextDraft));
    setPage(nextPage);
  }, [searchParams, searchParamsKey]);

  /**
   * Scroll restoration for the Root main scroll container.
   * Root uses a dedicated scroll container (height: 100vh + overflowY: auto),
   * so browser default scroll restoration may not work as expected.
   */
  useEffect(() => {
    const key = `${location.pathname}?${searchParams.toString()}`;
    const saved = sessionStorage.getItem(`scroll:${key}`);
    if (!saved) return;

    const el = document.getElementById('app-main-scroll');
    if (!el) return;

    const n = Number(saved);
    if (Number.isFinite(n)) {
      // Apply after layout to avoid fighting with table rendering.
      requestAnimationFrame(() => {
        el.scrollTop = n;
      });
    }

    sessionStorage.removeItem(`scroll:${key}`);
  }, [location.pathname, searchParams]);

  const { data, isLoading } = useInventoryList({
    page,
    pageSize: PAGE_SIZE,
    searchParam: appliedSearch,
  });

  // Count query must be based on the same appliedSearch to keep pagination consistent.
  const { data: totalCount, isLoading: isLoadingCount } = useInventoryCount(appliedSearch);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));

  const rows = (data?.data ?? []) as EsimInventoryResponse[];
  const isTableLoading = Boolean(isLoading || isLoadingCount);

  /* ------------------------------------------------------------------ */
  /* Options                                                            */
  /* ------------------------------------------------------------------ */

  const { data: stocksData } = useAllStockList();

  const stockOptions = useMemo<Array<Option<number>>>(() => {
    const list = stocksData?.data ?? [];
    return list.map((s) => ({ label: s.stockCode, value: s.stockId }));
  }, [stocksData]);

  const esimTypeOptions = useMemo<Array<Option<EsimType>>>(() => {
    return (ESIM_TYPE_OPTION_LIST as unknown as Array<Option<EsimType>>) ?? [];
  }, []);

  const esimStatusOptions = useMemo<Array<Option<EsimStatus>>>(() => {
    const values = Object.values(EsimStatus) as EsimStatus[];
    return values
      .map((v) => ({ value: v, label: getEsimStatusLabel(v) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const parameterTypeOptions = useMemo<Array<Option<EsimInventoryParameterType>>>(() => {
    const values = Object.values(EsimInventoryParameterType) as EsimInventoryParameterType[];
    return values
      .map((v) => ({ value: v, label: v.split('_').join(' ') }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Selected options (Autocomplete)                                     */
  /* ------------------------------------------------------------------ */

  const selectedStock = useMemo(() => {
    const id = draftSearch.stockId;
    if (typeof id !== 'number') return null;
    return stockOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.stockId, stockOptions]);

  const selectedEsimType = useMemo(() => {
    const v = draftSearch.esimType as EsimType | undefined;
    if (!v) return null;
    return esimTypeOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.esimType, esimTypeOptions]);

  const selectedEsimStatus = useMemo(() => {
    const v = draftSearch.esimStatus as EsimStatus | undefined;
    if (!v) return null;
    return esimStatusOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.esimStatus, esimStatusOptions]);

  const selectedParameterType = useMemo(() => {
    const v =
      (draftSearch.parameterType as EsimInventoryParameterType | undefined) ??
      EsimInventoryParameterType.LPA_URL;
    return parameterTypeOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.parameterType, parameterTypeOptions]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  /* ------------------------------------------------------------------ */
  /* setValue for shared Search components                               */
  /* ------------------------------------------------------------------ */

  const setValue: SetSearchValueFn<DraftSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next: DraftSearchForm = { ...prev };

      switch (key) {
        case 'parameterValue':
          next.parameterValue = typeof value === 'string' ? value : '';
          return next;

        case 'expireAtFrom':
        case 'expireAtTo':
          (next as any)[key] = value ? String(value) : undefined;
          return next;

        default:
          (next as any)[key] = value ?? undefined;
          return next;
      }
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Apply / Reset / Pagination                                          */
  /* ------------------------------------------------------------------ */

  const applyFilters = useCallback(
    (nextDraft?: DraftSearchForm) => {
      const next = nextDraft ?? draftSearch;
      setAppliedSearch(toAppliedParams(next));
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  const handleSearch = useCallback(() => applyFilters(), [applyFilters]);

  const handleReset = useCallback(() => {
    const cleared: DraftSearchForm = {
      stockId: undefined,
      esimType: undefined,
      esimStatus: undefined,
      expireAtFrom: undefined,
      expireAtTo: undefined,
      parameterType: EsimInventoryParameterType.LPA_URL,
      parameterValue: '',
    };

    setDraftSearch(cleared);
    applyFilters(cleared);
  }, [applyFilters]);

  const handleChangePage = useCallback(
    (next: number) => {
      const safeNext = clamp(next, 1, totalPages);
      setPage(safeNext);
      setSearchParams(buildUrlParams(draftSearch, safeNext));
    },
    [draftSearch, setSearchParams, totalPages],
  );

  /* ------------------------------------------------------------------ */
  /* Export                                                             */
  /* ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);
      const res = await exportEsimInventoriesExcel(appliedSearch);
      const fileName = `esim-inventories-${new Date().toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [appliedSearch, isExporting]);

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */

  const saveScrollForCurrentListView = useCallback(() => {
    const el = document.getElementById('app-main-scroll');
    if (!el) return;

    const key = `${location.pathname}?${searchParams.toString()}`;
    sessionStorage.setItem(`scroll:${key}`, String(el.scrollTop));
  }, [location.pathname, searchParams]);
  
  const handleOpenDetail = useCallback(
    (esimInventoryId: number) => {
      // Preserve the exact list URL (filters + page) and scroll position.
      const from = `${location.pathname}?${searchParams.toString()}`;
      saveScrollForCurrentListView();

      navigate(`/stocks/esim-inventory/${esimInventoryId}`, {
        state: { from },
      });
    },
    [location.pathname, navigate, saveScrollForCurrentListView, searchParams],
  );

  const columnsCount = 10;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box mt={2}>
        <EsimInventoriesSearchPanel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          draftSearch={draftSearch}
          setValue={setValue}
          stockOptions={stockOptions}
          esimTypeOptions={esimTypeOptions}
          esimStatusOptions={esimStatusOptions}
          parameterTypeOptions={parameterTypeOptions}
          selectedStock={selectedStock}
          selectedEsimType={selectedEsimType}
          selectedEsimStatus={selectedEsimStatus}
          selectedParameterType={selectedParameterType}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={handleExport}
          exportLoading={isExporting}
          autoSearch={PAGE_AUTO_SEARCH}
        />
      </Box>

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<EsimInventoryResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={1650}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No inventories found."
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>Stock</TableCell>
              <TableCell width={120}>Type</TableCell>
              <TableCell width={140} align="center">
                Status
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>Redeem</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Serial</TableCell>
              <TableCell sx={{ minWidth: 180 }}>ICCID</TableCell>
              <TableCell sx={{ minWidth: 160 }}>MSISDN</TableCell>
              <TableCell sx={{ minWidth: 280 }}>LPA URL</TableCell>
              <TableCell width={130} align="center">
                Expiry
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.esimInventoryId)}
          renderRow={(row, index) => {
            const id = Number(row.esimInventoryId);

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenDetail(id)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  {dashIfEmpty(row.stock?.stockCode)}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  {formatEsimType(row.esimType)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {renderStatusChip(row.esimStatus)}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Typography variant="body2" noWrap title={dashIfEmpty(row.redemptionCode)}>
                    {dashIfEmpty(row.redemptionCode)}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{dashIfEmpty(row.serialNo)}</TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Typography variant="body2" noWrap title={dashIfEmpty(row.iccid)}>
                    {dashIfEmpty(row.iccid)}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Typography variant="body2" noWrap title={dashIfEmpty(row.msisdn)}>
                    {dashIfEmpty(row.msisdn)}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Typography variant="body2" noWrap title={dashIfEmpty(row.lpaUrl)}>
                    {dashIfEmpty(row.lpaUrl)}
                  </Typography>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {row.expireAt ? timestampToDate(row.expireAt, 'YYYY-MM-DD') : '-'}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default EsimInventories;
